<template>
  <div class="tr-wrap">
    <div v-if="ansicht === 'raster'" class="tr-grid" :class="{compact}">
      <div v-for="(d, i) in tage" :key="i" class="tr-day" :class="{off: frei(d)}">
        <div class="tr-title">{{ wochentage[i] }}<small v-if="datum(i)">{{ datum(i) }}</small></div>
        <div class="tr-required"><small>Sollstunden</small><strong>{{ hhmm(d.zeit_soll) }}</strong></div>
        <div class="tr-field"><span>Wertigkeit</span>{{ hours(d.valence_hours) }}</div>
        <div class="tr-field"><span>Arbeitszeit</span>{{ frei(d) ? 'frei' : d.soll_zeit }}</div>
        <div class="tr-field"><span>Pause 1</span>{{ d.pause_1 || '00:00-00:00' }}</div>
        <div class="tr-field"><span>Pause 2</span>{{ d.pause_2 || '00:00-00:00' }}</div>
        <div v-if="d.schicht && showSchicht" class="tr-field"><span>Schicht</span>{{ d.schicht }}</div>
      </div>
    </div>
    <div v-else class="tr-cal">
      <div class="tr-axis"><span v-for="h in hoursAxis" :key="h" :style="{top: y(h * 60) + 'px'}">{{ String(h).padStart(2, '0') }}:00</span></div>
      <div v-for="(d, i) in tage" :key="i" class="tr-cal-day">
        <h4>{{ wochentage[i] }}</h4>
        <span v-if="frei(d)" class="tr-cal-off">frei</span>
        <div v-else class="tr-event" :style="eventStyle(d)">
          <strong>{{ d.soll_zeit }}</strong>
          <small>{{ hhmm(d.zeit_soll) }} Soll</small>
          <small v-if="pause(d.pause_1)">Pause {{ d.pause_1 }}</small>
          <small v-if="pause(d.pause_2)">Pause {{ d.pause_2 }}</small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {DateTime} from 'luxon'
import {fmtHHMM, fmtHours, WOCHENTAGE} from '@/shared/dienstplan_api'

const ZERO = '00:00-00:00'
const toMin = t => { const [h, m] = (t || '0:0').split(':').map(Number); return h * 60 + m }

// Read-only 7-day view of a pattern week (Monday … Sunday), styled like the
// existing Arbeitszeitraster screen; "kalender" shows a time line instead.
export default {
  name: 'TagesRaster',
  props: {
    tage: {type: Array, required: true},
    ansicht: {type: String, default: 'raster'},
    montag: {type: String, default: ''},
    compact: {type: Boolean, default: false},
    showSchicht: {type: Boolean, default: false},
  },
  data: () => ({wochentage: WOCHENTAGE, hoursAxis: [4, 7, 10, 13, 16, 19, 22]}),
  methods: {
    hhmm: fmtHHMM,
    hours: fmtHours,
    frei: d => !d || ((Number(d.zeit_soll) || 0) === 0 && (!d.soll_zeit || d.soll_zeit === ZERO)),
    pause: p => p && p !== ZERO,
    datum (i) {
      return this.montag ? DateTime.fromISO(this.montag).plus({days: i}).toFormat('dd.MM.') : ''
    },
    y: min => 30 + (min - 240) / 4,
    eventStyle (d) {
      const [a, b] = d.soll_zeit.split('-').map(toMin)
      const end = b < a ? b + 1440 : b
      return {top: this.y(a) + 'px', height: Math.max(48, (end - a) / 4) + 'px'}
    },
  },
}
</script>

<style scoped>
.tr-wrap {
  overflow: auto;
}
.tr-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(118px, 1fr));
  min-width: 840px;
}
.tr-day {
  border-right: 1px solid #aeb2b5;
  padding: 0 10px 8px;
}
.tr-day:last-child {
  border-right: 0;
}
.tr-title {
  text-align: center;
  font-weight: 500;
  padding: 10px 0 8px;
  font-size: 13px;
}
.tr-title small {
  display: block;
  color: #757575;
  font-weight: 400;
}
.tr-required {
  background: #f0f0f0;
  border-bottom: 1px dotted #aab0b3;
  padding: 6px 10px;
  margin-bottom: 14px;
  border-radius: 3px 3px 0 0;
}
.tr-required small {
  display: block;
  font-size: 10px;
}
.tr-required strong {
  font-weight: 400;
  font-size: 15px;
}
.tr-field {
  position: relative;
  border: 1px solid #a7adb3;
  border-radius: 4px;
  padding: 9px 8px 6px;
  margin-bottom: 14px;
  font-size: 13px;
  white-space: nowrap;
}
.tr-field span {
  position: absolute;
  top: -8px;
  left: 6px;
  background: #fff;
  padding: 0 3px;
  font-size: 10px;
  color: #37474f;
}
.compact .tr-field {
  margin-bottom: 10px;
  padding: 6px 6px 4px;
}
.tr-day.off {
  color: #9e9e9e;
}
.tr-cal {
  display: grid;
  grid-template-columns: 46px repeat(7, minmax(110px, 1fr));
  min-width: 840px;
  position: relative;
  height: 330px;
}
.tr-axis {
  position: relative;
  background: #fafafa;
  border-right: 1px solid #ddd;
}
.tr-axis span {
  position: absolute;
  right: 4px;
  font-size: 10px;
  color: #90a4ae;
  transform: translateY(-50%);
}
.tr-cal-day {
  position: relative;
  border-right: 1px solid #ddd;
  background: repeating-linear-gradient(to bottom, transparent 0, transparent 44px, #eceff1 44px, #eceff1 45px);
}
.tr-cal-day h4 {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: 0;
  height: 26px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  padding-top: 5px;
  background: #fff;
  border-bottom: 1px solid #ddd;
  z-index: 1;
}
.tr-cal-off {
  position: absolute;
  top: 120px;
  left: 0;
  right: 0;
  text-align: center;
  color: #9e9e9e;
  font-size: 12px;
}
.tr-event {
  position: absolute;
  left: 5px;
  right: 5px;
  background: #e8eaf6;
  border-left: 3px solid #3f51b5;
  border-radius: 2px;
  padding: 5px;
  font-size: 11px;
  color: #283593;
  overflow: hidden;
}
.tr-event strong,
.tr-event small {
  display: block;
}
</style>
